import { useState, useEffect, useCallback, useRef } from "react";

// ─── Sample images for demo ───────────────────────────────────────────────────
const SAMPLE_IMAGES = [
  { url: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=1200&q=90", name: "Atmos Watch", price: "$420", tag: "New" },
  { url: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=1200&q=90", name: "Velo Runner", price: "$195", tag: "" },
  { url: "https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=1200&q=90", name: "Sprint X", price: "$230", tag: "Sale" },
  { url: "https://images.unsplash.com/photo-1560343090-f0409e92791a?w=1200&q=90", name: "Mule 01", price: "$135", tag: "" },
  { url: "https://images.unsplash.com/photo-1585386959984-a4155224a1ad?w=1200&q=90", name: "Sérum Noir", price: "$78", tag: "" },
  { url: "https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=1200&q=90", name: "Sprint Y", price: "$175", tag: "" },
];

// ─── ImageViewer ──────────────────────────────────────────────────────────────
// Call setImageViewerIdx(image_url) to open
export function ImageViewer({ imageUrl, onClose }) {
  const [loaded, setLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [phase, setPhase] = useState("hidden");
  const imgRef = useRef(null);

  useEffect(() => {
    const t = requestAnimationFrame(() => setPhase("visible"));
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
      if (e.key === "+" || e.key === "=") setZoom(z => Math.min(4, z + 0.3));
      if (e.key === "-") setZoom(z => Math.max(0.5, z - 0.3));
      if (e.key === "0") { setZoom(1); setPan({ x: 0, y: 0 }); }
    };
    window.addEventListener("keydown", onKey);
    return () => { cancelAnimationFrame(t); window.removeEventListener("keydown", onKey); };
  }, []);

  const handleClose = () => {
    setPhase("hidden");
    setTimeout(onClose, 400);
  };

  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    e.preventDefault();
    setDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const onMouseMove = (e) => {
    if (!dragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const onMouseUp = () => setDragging(false);
  const onWheel = (e) => {
    e.preventDefault();
    setZoom(z => Math.min(4, Math.max(0.5, z - e.deltaY * 0.0012)));
  };
  const onDblClick = () => {
    if (zoom > 1) { setZoom(1); setPan({ x: 0, y: 0 }); }
    else setZoom(2.5);
  };

  const isIn = phase === "visible";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;1,400&family=Jost:wght@200;300;400&display=swap');

        .iv-root {
          position: fixed; inset: 0; z-index: 9999;
          display: flex; align-items: center; justify-content: center;
        }

        /* White frosted overlay — background content visible */
        .iv-veil {
          position: absolute; inset: 0;
         
         
        }
        .iv-veil.in { opacity: 1; }

        /* Panel */
        .iv-panel {
          position: relative; z-index: 1;
          width: min(90vw, 820px);
          background: #fff;
          border: 1px solid #e9e5e0;
          box-shadow: 0 4px 6px rgba(0,0,0,0.03), 0 20px 60px rgba(0,0,0,0.10), 0 48px 96px rgba(0,0,0,0.07);
          display: flex; flex-direction: column;
          opacity: 0;
          transform: translateY(40px) scale(0.98);
          transition: opacity 0.4s cubic-bezier(0.22,1,0.36,1),
                      transform 0.45s cubic-bezier(0.22,1,0.36,1);
        }
        .iv-panel.in { opacity: 1; transform: translateY(0) scale(1); }

        /* ── Header ── */
        .iv-hd {
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 32px; height: 60px;
          border-bottom: 1px solid #f0ece7;
        }
        .iv-hd-left { display: flex; align-items: center; gap: 14px; }
        .iv-eyebrow {
          font-family: 'Jost', sans-serif;
          font-size: 9.5px; font-weight: 300;
          letter-spacing: 0.24em; text-transform: uppercase; color: #b0a89e;
        }
        .iv-pipe { width: 1px; height: 16px; background: #e8e3de; }
        .iv-hdtitle {
          font-family: 'Playfair Display', serif;
          font-size: 16px; font-weight: 400;
          color: #1c1917; letter-spacing: 0.01em;
        }
        .iv-x {
          width: 34px; height: 34px;
          border: 1px solid #e4dfd8;
          border-radius: 50%;
          background: transparent;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.2s, border-color 0.2s, transform 0.25s;
        }
        .iv-x:hover { background: #1c1917; border-color: #1c1917; transform: rotate(90deg); }
        .iv-x:hover svg path { stroke: #fff; }
        .iv-x svg { display: block; }

        /* ── Image area ── */
        .iv-stage {
          position: relative;
          height: min(62vh, 540px);
          background: #f9f7f5;
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
          cursor: zoom-in;
          flex-shrink: 0;
        }
        .iv-stage.draggable { cursor: grab; }
        .iv-stage.draggable:active { cursor: grabbing; }

        /* animated grain texture */
        .iv-stage::after {
          content: '';
          position: absolute; inset: 0; pointer-events: none; z-index: 1;
          opacity: 0.018;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
        }

        .iv-img {
          display: block; max-width: 100%; max-height: 100%;
          width: auto; height: auto; object-fit: contain;
          opacity: 0; transition: opacity 0.5s ease;
          user-select: none; -webkit-user-drag: none;
          position: relative; z-index: 2;
        }
        .iv-img.rdy { opacity: 1; }

        /* Skeleton */
        .iv-skel {
          position: absolute; inset: 60px; z-index: 0;
          background: linear-gradient(105deg, #f0ece8 25%, #e8e2dc 45%, #f0ece8 65%);
          background-size: 400% 100%;
          animation: skpulse 1.8s ease infinite;
        }
        @keyframes skpulse {
          0%   { background-position: 100% 0; }
          100% { background-position: -100% 0; }
        }
        .iv-skel.gone { display: none; }

        /* Zoom hint pill */
        .iv-hint-pill {
          position: absolute; bottom: 18px; right: 20px; z-index: 3;
          font-family: 'Jost', sans-serif;
          font-size: 9.5px; font-weight: 300; letter-spacing: 0.14em;
          color: #a0988e;
          background: rgba(255,255,255,0.92);
          border: 1px solid #e8e3de;
          padding: 5px 13px; border-radius: 100px;
          pointer-events: none;
          transition: opacity 0.25s;
        }

        /* ── Footer ── */
        .iv-ft {
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 32px; height: 54px;
          border-top: 1px solid #f0ece7;
        }

        /* Zoom pill control */
        .iv-zpill {
          display: flex; align-items: center;
          border: 1px solid #e4dfd8; border-radius: 100px;
          overflow: hidden;
        }
        .iv-zpill-btn {
          width: 34px; height: 30px;
          border: none; background: transparent; cursor: pointer;
          font-size: 16px; color: #888; line-height: 1;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.15s, color 0.15s;
        }
        .iv-zpill-btn:hover:not(:disabled) { background: #f5f1ed; color: #1c1917; }
        .iv-zpill-btn:disabled { opacity: 0.28; cursor: default; }
        .iv-zpill-div { width: 1px; height: 18px; background: #e4dfd8; }
        .iv-zpill-num {
          font-family: 'Jost', sans-serif;
          font-size: 10px; font-weight: 300; letter-spacing: 0.1em; color: #999;
          min-width: 44px; text-align: center;
        }

        /* Track bar */
        .iv-track-wrap {
          display: flex; align-items: center; gap: 10px;
        }
        .iv-track {
          width: 72px; height: 2px; background: #ede9e4; border-radius: 2px; overflow: hidden;
        }
        .iv-track-fill {
          height: 100%; border-radius: 2px;
          background: linear-gradient(90deg, #c8b99a, #1c1917);
          transition: width 0.12s ease;
        }

        .iv-reset {
          font-family: 'Jost', sans-serif;
          font-size: 9.5px; font-weight: 300; letter-spacing: 0.18em;
          text-transform: uppercase; color: #c8c0b8;
          background: none; border: none; cursor: pointer; padding: 0;
          transition: color 0.18s;
        }
        .iv-reset:hover { color: #1c1917; }

        .iv-esc {
          font-family: 'Jost', sans-serif;
          font-size: 9px; font-weight: 200; letter-spacing: 0.16em;
          text-transform: uppercase; color: #d5cfc9;
        }
      `}</style>

      <div
        className="iv-root"
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onWheel={onWheel}
      >
        <div className={`iv-veil ${isIn ? "in" : ""}`} onClick={handleClose} />

        <div className={`iv-panel ${isIn ? "in" : ""}`}>
          {/* Header */}
          <div className="iv-hd">
            <div className="iv-hd-left">
              <span className="iv-eyebrow">Quick View</span>
              <div className="iv-pipe" />
              <span className="iv-hdtitle">Product Preview</span>
            </div>
            <button className="iv-x" onClick={handleClose} aria-label="Close">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1 1L9 9M9 1L1 9" stroke="#1c1917" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Image stage */}
          <div
            className={`iv-stage ${zoom > 1 ? "draggable" : ""}`}
            onMouseDown={onMouseDown}
            onDoubleClick={onDblClick}
          >
            <div className={`iv-skel ${loaded ? "gone" : ""}`} />
            <img
              ref={imgRef}
              src={imageUrl}
              className={`iv-img ${loaded ? "rdy" : ""}`}
              style={{
                transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                transition: dragging ? "none" : "transform 0.14s ease, opacity 0.5s ease",
              }}
              onLoad={() => setLoaded(true)}
              draggable={false}
              alt="Product"
            />
            {loaded && (
              <div className="iv-hint-pill">
                {zoom > 1
                  ? `${Math.round(zoom * 100)}%  ·  drag to pan`
                  : "scroll or double-click to zoom"}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="iv-ft">
            <div className="iv-zpill">
              <button className="iv-zpill-btn" onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} disabled={zoom <= 0.5}>−</button>
              <div className="iv-zpill-div" />
              <span className="iv-zpill-num">{Math.round(zoom * 100)}%</span>
              <div className="iv-zpill-div" />
              <button className="iv-zpill-btn" onClick={() => setZoom(z => Math.min(4, z + 0.25))} disabled={zoom >= 4}>+</button>
            </div>

            <div className="iv-track-wrap">
              <div className="iv-track">
                <div className="iv-track-fill" style={{ width: `${((zoom - 0.5) / 3.5) * 100}%` }} />
              </div>
              <button className="iv-reset" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}>Reset</button>
            </div>

            <span className="iv-esc">Esc to close</span>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Demo storefront ──────────────────────────────────────────────────────────
export default function App() {
  const [activeImage, setActiveImage] = useState(null);

  // ← Expose this wherever you need it
  const setImageViewerIdx = useCallback((image_url) => setActiveImage(image_url), []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&family=Jost:wght@200;300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #fff; -webkit-font-smoothing: antialiased; }

        .pg { min-height: 100vh; background: #fff; font-family: 'Jost', sans-serif; }

        /* Nav */
        .nav {
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 48px; height: 72px;
          border-bottom: 1px solid #eeebe7;
          position: sticky; top: 0; background: rgba(255,255,255,0.94);
          backdrop-filter: blur(12px); z-index: 100;
        }
        .nav-logo {
          font-family: 'Playfair Display', serif;
          font-size: 19px; font-weight: 400; letter-spacing: 0.08em; color: #1c1917;
        }
        .nav-links { display: flex; gap: 32px; }
        .nav-link {
          font-size: 10.5px; font-weight: 300; letter-spacing: 0.2em;
          text-transform: uppercase; color: #aaa; cursor: pointer;
          transition: color 0.2s; text-decoration: none;
        }
        .nav-link:hover { color: #1c1917; }

        /* Content */
        .content { padding: 64px 48px 100px; }

        .section-row {
          display: flex; align-items: baseline; justify-content: space-between;
          margin-bottom: 44px;
        }
        .section-title {
          font-family: 'Playfair Display', serif;
          font-size: 30px; font-weight: 400; color: #1c1917; letter-spacing: 0.01em;
        }
        .section-sub {
          font-size: 10.5px; font-weight: 300; letter-spacing: 0.14em;
          text-transform: uppercase; color: #c8c0b8;
        }

        /* Grid */
        .grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 3px;
        }

        /* Card */
        .card {
          position: relative; cursor: pointer; background: #f8f6f3;
          overflow: hidden; display: flex; flex-direction: column;
        }
        .card-thumb {
          aspect-ratio: 3/4; overflow: hidden; position: relative;
        }
        .card-img {
          width: 100%; height: 100%; object-fit: cover; display: block;
          transition: transform 0.75s cubic-bezier(0.25,0.46,0.45,0.94);
        }
        .card:hover .card-img { transform: scale(1.07); }

        /* Hover scrim + pill */
        .card-scrim {
          position: absolute; inset: 0;
          background: rgba(255,255,255,0);
          transition: background 0.35s;
          display: flex; align-items: center; justify-content: center;
        }
        .card:hover .card-scrim { background: rgba(255,255,255,0.18); }
        .card-qv {
          font-family: 'Jost', sans-serif;
          font-size: 9.5px; font-weight: 400; letter-spacing: 0.26em;
          text-transform: uppercase; color: #1c1917;
          background: rgba(255,255,255,0.96);
          border: 1px solid rgba(28,25,23,0.1);
          padding: 10px 20px;
          opacity: 0; transform: translateY(10px);
          transition: opacity 0.28s, transform 0.32s;
          pointer-events: none;
        }
        .card:hover .card-qv { opacity: 1; transform: translateY(0); }

        /* Badge */
        .badge {
          position: absolute; top: 14px; left: 14px;
          font-size: 7.5px; font-weight: 400; letter-spacing: 0.22em;
          text-transform: uppercase; color: #fff; background: #1c1917;
          padding: 4px 9px;
        }
        .badge.sale { background: #c8503a; }

        /* Card foot */
        .card-foot {
          padding: 18px 22px 22px;
          background: #fff;
          border-top: 1px solid #f0ece7;
          display: flex; align-items: flex-end; justify-content: space-between;
        }
        .card-name {
          font-family: 'Playfair Display', serif;
          font-size: 15px; font-weight: 400; color: #1c1917; letter-spacing: 0.01em;
        }
        .card-price {
          font-family: 'Jost', sans-serif;
          font-size: 12px; font-weight: 300; letter-spacing: 0.08em; color: #b0a89e;
        }
      `}</style>

      <div className="pg">
        <nav className="nav">
          <div className="nav-logo">Maison</div>
          <div className="nav-links">
            {["New", "Women", "Men", "Sale", "About"].map(l => (
              <span key={l} className="nav-link">{l}</span>
            ))}
          </div>
        </nav>

        <div className="content">
          <div className="section-row">
            <h2 className="section-title">New Arrivals</h2>
            <span className="section-sub">{SAMPLE_IMAGES.length} pieces</span>
          </div>

          <div className="grid">
            {SAMPLE_IMAGES.map((p, i) => (
              <div key={i} className="card" onClick={() => setImageViewerIdx(p.url)}>
                <div className="card-thumb">
                  <img src={p.url} className="card-img" alt={p.name} loading="lazy" />
                  <div className="card-scrim">
                    <div className="card-qv">Quick View</div>
                  </div>
                  {p.tag && (
                    <div className={`badge ${p.tag === "Sale" ? "sale" : ""}`}>{p.tag}</div>
                  )}
                </div>
                <div className="card-foot">
                  <span className="card-name">{p.name}</span>
                  <span className="card-price">{p.price}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {activeImage && (
        <ImageViewer imageUrl={activeImage} onClose={() => setActiveImage(null)} />
      )}
    </>
  );
}